import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../../firebase/firebase';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import { Loader, Card, Image, Icon, Grid, Container, Header, Message, Button, Modal } from 'semantic-ui-react';
import ReusableBoatCard from '../common/BoatCard';
import ReviewList from '../SelectBoat/ReviewList';
import MessageOwner from '../SelectBoat/MessageOwner';
import Footer from '../Footer/Footer';
import userIcon from '../SelectBoat/images/user.png';
import { toast } from 'react-toastify';

const PublicProfile = () => {
  const { userId } = useParams();
  const [profile, setProfile] = useState(null);
  const [boats, setBoats] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [messageModalOpen, setMessageModalOpen] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      if (!userId) return;
      
      setLoading(true);
      setError(null);
      try {
        // 1. Fetch Public Profile
        const functions = getFunctions();
        const getPublicProfileFn = httpsCallable(functions, 'getPublicProfile');
        // Pass userId as identifier since it might be a username now
        const profileResult = await getPublicProfileFn({ identifier: userId });
        
        if (!profileResult.data) {
            setError("User not found.");
            setLoading(false);
            return;
        }
        const profileData = profileResult.data;
        setProfile(profileData);

        // 2. Fetch User's Active Boats
        const boatsRef = collection(db, 'boats', profileData.id, 'userBoats');
        const q = query(boatsRef, where('active', '==', true));
        const querySnapshot = await getDocs(q);
        
        const boatsData = querySnapshot.docs.map(doc => {
            const data = doc.data();
            return {
                uid: doc.id,
                ...data,
                // Ensure dates are serialized for components that expect strings
                createdAt: data.createdAt?.toDate ? data.createdAt.toDate().toISOString() : data.createdAt,
                featuredUntil: data.featuredUntil?.toDate ? data.featuredUntil.toDate().toISOString() : data.featuredUntil,
                // Map unavailableDates if needed by BoatCard
                unavailableDates: (data.unavailableDates || []).map(range => ({
                    start: range.start?.toDate ? range.start.toDate().toISOString() : range.start,
                    end: range.end?.toDate ? range.end.toDate().toISOString() : range.end,
                })),
            };
        });
        setBoats(boatsData);

        // 3. Fetch User's Reviews
        const reviewsRef = collection(db, 'users', profileData.id, 'reviews');
        const reviewsQuery = query(reviewsRef, orderBy('createdAt', 'desc'));
        const reviewsSnapshot = await getDocs(reviewsQuery);
        const reviewsData = reviewsSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
        }));
        setReviews(reviewsData);

      } catch (err) {
        console.error("Error loading public profile:", err);
        setError("Sign in or refine your search to view this feature.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [userId]);

  if (loading) {
    return (
        <div style={{ minHeight: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Loader active inline="centered" size="large">Loading Profile...</Loader>
        </div>
    );
  }

  if (error || !profile) {
    return (
        <Container style={{ marginTop: '50px', minHeight: '60vh' }}>
            <Message negative>
                <Message.Header>Error</Message.Header>
                <p>{error || "Profile not found."}</p>
            </Message>
        </Container>
    );
  }

  return (
    <div style={{ alignItems: 'center', minHeight: '100vh', backgroundColor: '#f9f9f9' }}>
      <Container style={{ flex: 1, padding: '40px 0' }}>
        <Grid stackable>
          <Grid.Column width={5}>
            <Card fluid style={{ height: '400px' }}>
              <Image 
                src={profile.photoURL || userIcon} 
                wrapped 
                ui={false} 
                style={{ backgroundColor: '#f0f0f0', maxHeight: '100px', height: 'auto', width: '100px', borderRadius: '50%', objectFit: 'cover', margin: '10px', marginLeft: '35%' }}
                onError={(e) => { e.target.src = userIcon; }}
              />
              <Card.Content>
                <Card.Header style={{ wordBreak: 'break-word' }}>{profile.displayName}</Card.Header>
                <Card.Meta>
                  <span className='date'>Boat Owner</span>
                </Card.Meta>
                <Card.Description>
                  {profile.captainLicenseVerified ? (
                    <div style={{ color: '#2185d0', display: 'flex', alignItems: 'center', marginTop: '10px' }}>
                      <Icon name='check circle' size='large' />
                      <span style={{ marginLeft: '5px', fontWeight: 'bold' }}>Verified Captain</span>
                    </div>
                  ) : (
                    <div style={{ color: '#666', marginTop: '10px' }}>
                        <Icon name='user' /> Member
                    </div>
                  )}
                </Card.Description>
              </Card.Content>
              <Card.Content extra>
                <div className='ui two buttons'>
                  <Button basic color='blue' onClick={() => setMessageModalOpen(true)}>
                    <Icon name='mail' /> Contact
                  </Button>
                  <Button basic color='green' as={Link} to={profile.username ? `/store/${profile.username}` : `/store/${profile.id}`}>
                    <Icon name='shopping bag' /> Store
                  </Button>
                </div>
              </Card.Content>
            </Card>
          </Grid.Column>
          
          <Grid.Column width={11}>
            <Header as='h2' dividing>
              Boats by {profile.displayName}
            </Header>
            
            {boats.length > 0 ? (
              <Grid stackable columns={2}>
                  {boats.map(boat => (
                      <Grid.Column key={boat.uid}>
                          <ReusableBoatCard boat={boat} />
                      </Grid.Column>
                  ))}
              </Grid>
            ) : (
              <Message info>
                <Message.Header>No active listings</Message.Header>
                <p>This user currently has no boats listed for rent.</p>
              </Message>
            )}

            <Header as='h3' dividing style={{ marginTop: '40px' }}>
              Reviews
            </Header>
            <ReviewList 
                reviews={reviews} 
                loading={false} 
                onEdit={() => toast.info("Please go to the boat page to edit your review.")}
                onDelete={() => toast.info("Please go to the boat page to delete your review.")}
            />
          </Grid.Column>
        </Grid>

        <Modal
            open={messageModalOpen}
            onClose={() => setMessageModalOpen(false)}
            size='small'
            closeIcon
        >
            <Modal.Content>
                <MessageOwner
                    boatOwnerUID={profile.id}
                    messageCloseModal={() => setMessageModalOpen(false)}
                    title={`Message ${profile.displayName}`}
                />
            </Modal.Content>
        </Modal>
      </Container>
      <Footer />
    </div>
  );
};

export default PublicProfile;
